import { Injectable } from '@angular/core';
import { CsSelectPopover } from 'components/cs-select-popover/cs-select-popover';
import { BackdropMode } from 'components/popover-host/popover-host';
import { PopoverController } from 'providers/popover-controller';
import { TranslatedString } from 'models/translated-string';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { toSelectPopoverOption } from './popup-menu-options';

export interface PopupMenuSubmenu {
  title?: TranslatedString;  // title for mobile submenu header
  buttons: PopupMenuButton[];
}

export interface PopupMenuButton {
  text: TranslatedString;
  // actual value to return (defaults to text if not provided)
  value?: string;
  icon?: string;
  fillIcon?: boolean;
  disabled?: boolean;
  // if the button is a label, it will not be clickable
  isLabel?: boolean;
  // used only if selectMultiple is enabled
  selectState?: boolean;
  cssClass?: string;
  hidden?: boolean;
  // in multi-select mode the menu stays open; if the handler is async, the menu redraws its rows
  // once it settles, so states the handler changed on these buttons after an await are shown
  handler?: (event?: Event) => void | Promise<void>;
  // if true, shows a help icon on the right side of the menu item
  helpIcon?: boolean;
  // tooltip text for the help icon
  description?: TranslatedString;
  // control type for multi-select mode
  controlType?: 'checkbox' | 'toggle' | 'radio';
  // nested submenu (shows on hover on desktop, navigates on mobile)
  submenu?: PopupMenuSubmenu;
}

export interface PopupMenuSubsection {
  header: TranslatedString;
  buttons: PopupMenuButton[];
}

export interface PopupMenuOptions {
  // header text for the popover
  title?: TranslatedString;
  // flat list of buttons (use this OR subsections)
  buttons?: PopupMenuButton[];
  // grouped options with section headers
  subsections?: PopupMenuSubsection[];
  // pre-selected value for single-select mode
  value?: string;
  selectMultiple?: boolean;
  // show the popover's search box, which filters the rows as the user types
  enableSearch?: boolean;
  // optional anchor element. When omitted the popover presents without an
  // anchor (e.g. a bottom sheet on small screens) — used by menus opened
  // from a handler rather than a tap event. The present() impl below
  // already guards the undefined case.
  targetFromElement?: HTMLElement;
  targetToElement?: HTMLElement;
  horizontalAlign?: 'left' | 'right';
  // show back button in header (for nested menus on mobile)
  backButton?: boolean;
  /**
   * Present as a centred dialog on a large screen, instead of a dropdown hung off the control
   * that opened it. For a menu that is a page of settings rather than a short list of actions
   * (the Add Foods Filters menu, #5549). A phone shows the same bottom sheet either way.
   *
   * It works by anchoring to `document.body`, which is how the host has always been told to
   * centre something — see `alert-controller.ts`, and the `targetToElement === document.body`
   * branches in `popover-host.ts`. The anchor's width is deliberately NOT copied to the menu
   * here, or a body anchor would make it as wide as the screen.
   */
  centered?: boolean;
  /**
   * One extra action in the menu's title bar, beside the title — for something that acts on the
   * menu as a whole rather than on one row (the Add Foods "Reset", #5549). Running it closes the
   * menu, because the rows were drawn from the state it just changed.
   *
   * Only drawn where there IS a title bar: a centred dialog, or a phone's bottom sheet.
   */
  headerAction?: { text: TranslatedString; handler: () => void };
}

@Injectable()
export class PopupMenuProvider {
  constructor(private popoverCtrl: PopoverController) { }

  async createMenu(opts: PopupMenuOptions) {
    const isSmallScreen = hasClientSmallScreen();

    // map flat buttons to options (if provided)
    const options = opts.buttons?.map(toSelectPopoverOption);

    // map subsections (if provided)
    const subsections = opts.subsections?.map(section => ({
      header: section.header,
      options: section.buttons.map(toSelectPopoverOption),
    }));

    return this.popoverCtrl.present(CsSelectPopover,
      {
        title: opts.title,
        options: subsections ? undefined : options,
        subsections: subsections,
        value: opts.value,
        selectMultiple: opts.selectMultiple,
        searchable: opts.enableSearch,
        targetWidth: !opts.centered && opts.targetFromElement ? opts.targetFromElement.offsetWidth : undefined,
        bottomSheet: isSmallScreen,
        centered: !!opts.centered && !isSmallScreen,
        backButton: opts.backButton,
        headerAction: opts.headerAction,
      },
      {
        targetFromElement: opts.centered ? document.body : opts.targetFromElement,
        targetToElement: opts.centered ? document.body : (opts.targetToElement || opts.targetFromElement),
        animate: true,
        verticalAlign: 'bottom',
        horizontalAlign: opts.horizontalAlign ?? 'left',
        overlayPreviousPopover: true,
        bottomSheet: isSmallScreen,
        // visible backdrop on mobile (darkening) and behind a centred dialog, invisible on a
        // desktop dropdown (click detection only)
        backdropMode: isSmallScreen || opts.centered ? BackdropMode.Visible : BackdropMode.Invisible,
      }
    );
  }
}
